import React from 'react'
import { Link } from '@inertiajs/react'
import { ArrowRight, ExternalLink } from 'lucide-react'
import DashboardCard from '@/components/dashboard/DashboardCard'
import CompetitorBadge from '@/components/market/CompetitorBadge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatCount, formatMoney } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { MarketCompetitor, MarketYou } from '@/types/market'

/**
 * Who the competitors are, over the tile window: distance, stars, reviews, their typical list
 * price against yours, and how often they were cheaper, sold out or asked for a minimum stay. Your
 * own row leads. A table on desktop, one card per hotel on a phone.
 */

function nights(n: number): string {
  return `${formatCount(n)} ${n === 1 ? 'night' : 'nights'}`
}

function Hint({ children, hint }: { children: React.ReactNode; hint: string }) {
  return (
    <span
      title={hint}
      className="cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-4"
    >
      {children}
    </span>
  )
}

function HotelLink({ name, url, className }: { name: string; url: string | null; className?: string }) {
  if (!url) return <span className={cn('truncate', className)}>{name}</span>
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={`${name} on Booking.com`}
      className={cn(
        'inline-flex min-w-0 cursor-pointer items-center gap-1 rounded hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        className
      )}
    >
      <span className="truncate">{name}</span>
      <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="sr-only">(opens Booking.com in a new tab)</span>
    </a>
  )
}

function CheckedCell({ competitor }: { competitor: MarketCompetitor }) {
  if (competitor.checkStatus === 'missing') {
    return (
      <span className="inline-flex items-start gap-1.5 text-xs text-amber-700" title={competitor.checkLabel}>
        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
        <span>
          {competitor.lastCheckedLabel}
          <span className="block text-[11px] text-amber-700/80">{competitor.checkLabel}</span>
        </span>
      </span>
    )
  }
  return <span className="text-xs text-muted-foreground">{competitor.lastCheckedLabel}</span>
}

export default function CompetitorsTable({
  competitors,
  you,
  numbers,
  currency,
  windowNights,
  compsetHref,
  canManage,
}: {
  competitors: MarketCompetitor[]
  you: MarketYou
  numbers: Map<number, number>
  currency: string
  windowNights: number
  compsetHref: string
  canManage: boolean
}) {
  const money = (v: number) => formatMoney(v, currency)
  const dash = <span className="text-xs text-muted-foreground">--</span>

  return (
    <DashboardCard
      title="Competitors"
      description={`Your compset over the next ${windowNights} nights`}
      padded={false}
      action={
        <Link
          href={compsetHref}
          className="inline-flex h-8 cursor-pointer items-center gap-1 rounded-lg px-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {canManage ? 'Edit compset' : 'View compset'}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      }
    >
      {/* Desktop table */}
      <div className="hidden overflow-x-auto md:block">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-5">Hotel</TableHead>
              <TableHead>Distance</TableHead>
              <TableHead>Stars</TableHead>
              <TableHead>Reviews</TableHead>
              <TableHead className="text-right">
                <Hint hint={`The middle list price across the next ${windowNights} nights.`}>Typical price</Hint>
              </TableHead>
              <TableHead className="text-right">
                <Hint hint="Nights when this hotel's list price was below yours.">Cheaper than you</Hint>
              </TableHead>
              <TableHead className="text-right">
                <Hint hint="Nights with no room for two available.">Sold out</Hint>
              </TableHead>
              <TableHead>
                <Hint hint="Nights that needed a stay of more than one night to book.">Minimum stay</Hint>
              </TableHead>
              <TableHead className="pr-5">Last checked</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow className="bg-primary/[0.04] hover:bg-primary/[0.06]" data-testid="competitors-you">
              <TableCell className="relative max-w-[260px] pl-5">
                <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-primary" />
                <div className="flex min-w-0 items-center gap-2">
                  <CompetitorBadge isYou />
                  <HotelLink name={you.name} url={you.bookingComUrl} className="text-sm font-medium text-foreground" />
                </div>
              </TableCell>
              <TableCell>{dash}</TableCell>
              <TableCell className="text-sm text-foreground">{you.starsLabel ?? dash}</TableCell>
              <TableCell className="text-sm text-foreground">{you.reviewLabel ?? dash}</TableCell>
              <TableCell className="text-right">
                {you.typicalPrice != null ? (
                  <span className="text-sm font-semibold tabular-nums text-primary">{money(you.typicalPrice)}</span>
                ) : (
                  dash
                )}
                {you.typicalBookingComPrice != null && (
                  <span className="block text-[11px] tabular-nums text-muted-foreground">
                    {money(you.typicalBookingComPrice)} on Booking.com
                  </span>
                )}
              </TableCell>
              <TableCell className="text-right">{dash}</TableCell>
              <TableCell className="text-right">{dash}</TableCell>
              <TableCell>{dash}</TableCell>
              <TableCell className="pr-5 text-xs text-muted-foreground">{you.lastCheckedLabel ?? dash}</TableCell>
            </TableRow>
            {competitors.map((c) => (
              <TableRow key={c.compsetId}>
                <TableCell className="max-w-[260px] pl-5">
                  <div className="flex min-w-0 items-center gap-2">
                    <CompetitorBadge number={numbers.get(c.marketPropertyId)} />
                    <HotelLink name={c.name} url={c.bookingComUrl} className="text-sm font-medium text-foreground" />
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                  {c.distanceLabel ?? dash}
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm text-foreground">{c.starsLabel}</TableCell>
                <TableCell className="whitespace-nowrap text-sm text-foreground">{c.reviewLabel ?? dash}</TableCell>
                <TableCell className="text-right">
                  {c.typicalPrice != null ? (
                    <span className="text-sm font-semibold tabular-nums text-foreground">{money(c.typicalPrice)}</span>
                  ) : (
                    dash
                  )}
                  {c.vsYouLabel && (
                    <span className="block whitespace-nowrap text-[11px] text-muted-foreground">{c.vsYouLabel}</span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right text-sm tabular-nums text-foreground">
                  {nights(c.nightsCheaperThanYou)}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right text-sm tabular-nums text-foreground">
                  {nights(c.soldOutNights)}
                </TableCell>
                <TableCell className="text-sm text-foreground">
                  {c.minStayLabel ?? <span className="text-muted-foreground">None</span>}
                </TableCell>
                <TableCell className="whitespace-nowrap pr-5">
                  <CheckedCell competitor={c} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Phone cards */}
      <ul className="divide-y divide-border/40 md:hidden">
        <li className="relative bg-primary/[0.04] px-4 py-4">
          <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-primary" />
          <div className="flex min-w-0 items-center gap-2">
            <CompetitorBadge isYou />
            <HotelLink name={you.name} url={you.bookingComUrl} className="text-sm font-semibold text-foreground" />
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
            <div>
              <dt className="text-muted-foreground">Typical price</dt>
              <dd className="font-semibold tabular-nums text-primary">
                {you.typicalPrice != null ? money(you.typicalPrice) : '--'}
              </dd>
              {you.typicalBookingComPrice != null && (
                <dd className="text-[11px] tabular-nums text-muted-foreground">
                  {money(you.typicalBookingComPrice)} on Booking.com
                </dd>
              )}
            </div>
            <div>
              <dt className="text-muted-foreground">Reviews</dt>
              <dd className="text-foreground">{you.reviewLabel ?? '--'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Stars</dt>
              <dd className="text-foreground">{you.starsLabel ?? '--'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Last checked</dt>
              <dd className="text-foreground">{you.lastCheckedLabel ?? '--'}</dd>
            </div>
          </dl>
        </li>
        {competitors.map((c) => (
          <li key={c.compsetId} className="px-4 py-4">
            <div className="flex min-w-0 items-center gap-2">
              <CompetitorBadge number={numbers.get(c.marketPropertyId)} />
              <HotelLink name={c.name} url={c.bookingComUrl} className="text-sm font-semibold text-foreground" />
            </div>
            <p className="mt-1 pl-6 text-[11px] text-muted-foreground">
              {[c.distanceLabel, c.starsLabel, c.reviewLabel].filter(Boolean).join(' · ')}
            </p>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
              <div>
                <dt className="text-muted-foreground">Typical price</dt>
                <dd className="font-semibold tabular-nums text-foreground">
                  {c.typicalPrice != null ? money(c.typicalPrice) : '--'}
                </dd>
                {c.vsYouLabel && <dd className="text-[11px] text-muted-foreground">{c.vsYouLabel}</dd>}
              </div>
              <div>
                <dt className="text-muted-foreground">Cheaper than you</dt>
                <dd className="tabular-nums text-foreground">{nights(c.nightsCheaperThanYou)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Sold out</dt>
                <dd className="tabular-nums text-foreground">{nights(c.soldOutNights)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Minimum stay</dt>
                <dd className="text-foreground">{c.minStayLabel ?? 'None'}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-muted-foreground">Last checked</dt>
                <dd>
                  <CheckedCell competitor={c} />
                </dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>
    </DashboardCard>
  )
}
